import type { ItemId } from "../items";

/**
 * Full duotone: the art's luma is mapped between the world's *current* shadow
 * and highlight, so an icon is drawn in the same two colours everything else is
 * — cold at midnight, warm at dusk. 1 is complete duotone, 0 is the art as
 * supplied.
 */
const TINT_MIX = 1;
const sources = new Map<ItemId, HTMLImageElement>();
const cache = new Map<string, HTMLCanvasElement>();

export function loadItemArt(ids: ItemId[]): Promise<void> {
  return Promise.all(
    ids.map(
      (id) =>
        new Promise<void>((resolve) => {
          const image = new Image();
          image.onload = (): void => {
            sources.set(id, image);
            resolve();
          };
          image.onerror = (): void => resolve();
          image.src = `items/${id}.png`;
        }),
    ),
  ).then(() => undefined);
}

/** quantised so the cache does not grow as the sky drifts through the day */
const band = (value: number): number => Math.round(value / 6) * 6;

export function itemArt(
  id: ItemId,
  size: number,
  shadow: number,
  highlight: number,
): HTMLCanvasElement | null {
  const s = [band(shadow >> 16), band((shadow >> 8) & 255), band(shadow & 255)];
  const h = [band(highlight >> 16), band((highlight >> 8) & 255), band(highlight & 255)];
  const key = `${id}:${size}:${s.join(",")}:${h.join(",")}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const source = sources.get(id);
  if (!source) return null;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d");
  if (!context) return null;

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(source, 0, 0, size, size);

  const image = context.getImageData(0, 0, size, size);
  const mix = TINT_MIX;
  for (let i = 0; i < image.data.length; i += 4) {
    if (image.data[i + 3] === 0) continue;
    const r = image.data[i]!;
    const g = image.data[i + 1]!;
    const b = image.data[i + 2]!;
    const t = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    image.data[i] = Math.round(r + (s[0]! + (h[0]! - s[0]!) * t - r) * mix);
    image.data[i + 1] = Math.round(g + (s[1]! + (h[1]! - s[1]!) * t - g) * mix);
    image.data[i + 2] = Math.round(b + (s[2]! + (h[2]! - s[2]!) * t - b) * mix);
    image.data[i + 3] = 255;
  }
  context.putImageData(image, 0, 0);

  cache.set(key, canvas);
  return canvas;
}
